(ns arthur.domain.pose
  "An instance's explicit, held choices of source pose for each shape group.

  A track is {local-frame -> source-frame}. The key is when the cut happens;
  the value is the frozen pose to read. Skipped source frames remain available.

  AND THE PRESERVE-SNAP, which is the same question asked where nobody has
  answered it by hand. A grid slot already picks a native frame — the latest one
  at or before its time, `node/placed-frame` floored — and that pick has no opinion about
  content, so at 12fps out of 30 it drops two frames in three and cannot know that
  one of them is where the mouth shut. The snap gives it one: a slot reads the
  latest MARKED frame inside its own gap. `marks` is where the marks come from and
  `snapped-frame` is the rule; `domain/symbol` seats the rule as the DEFAULT pose,
  so a hand cut still beats it without anything having to say so."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.node :as node]))

(defn prepare
  "Sort pose tracks once when building a resolver."
  [tracks]
  (into {}
        (map (fn [[group entries]]
               [group (vec (sort-by first entries))]))
        tracks))

;; ---------------------------------------------------------------------------
;; the preserve-snap: which frames are worth landing on

(def ^:private closure-cuts
  "The `[:vis]` provenances that are a CLOSURE, and therefore a frame worth
  landing on.

  A SKIPPED FRAME, A HIDDEN FEATURE AND AN ABSENT MEASUREMENT ARE THREE
  DIFFERENT FACTS, and marks being derived from a visibility cut is exactly what
  makes that easy to blur. `flow/freeze` writes `[:vis]` for three different
  reasons: the mouth's aperture against the take's peak, `condition/resolve-blink`
  with its cut and dwell, and whether a teeth contour could be extracted. The
  first two are a part CLOSING — a thing the picture should land on. The third is
  a feature not being there to draw, which says nothing about a performance, and
  nor does a `[:vis]` somebody keyed by hand to switch a part off.

  So this is a whitelist of two and not a test for `:generated`: reading every
  stored `[:vis]` would snap the grid onto the frames a part happened to be
  missing on, which is the cadence being dragged about by an absence."
  #{:roto/mouth-aperture :roto/blink})

(defn- shut-frames
  "The frames channel `c` calls shut, or nil when it is not a closure cut.

  Sampled through a CURSOR rather than `ch/value-at` because the frames are read
  in order: `value-at` rebuilds the sorted key index per call and the two are
  required to agree exactly, so the sequential reader is the cheap half of an
  equality the model already guarantees.

  `false?` and not falsiness. `ch/absent` is not a closure — a frame the subject
  was not on has no mouth to be shut — and a dense `[:vis]` yields 0, which is
  truthy in CLJS, so neither obvious test is right. `symbol/visible?` insists on
  the same boolean for the same reason."
  [c frames store]
  (when (contains? closure-cuts (:by (:generated c)))
    (let [cur (ch/cursor c store)]
      (into [] (filter #(false? (ch/sample! cur %))) (range frames)))))

(defn marks
  "Frames worth landing on, per pose group: `{group -> ascending frames}`, or nil
  where nothing is marked.

  PRECOMPUTED WHEN THE RESOLVER IS BUILT, beside `prepare`,
  and this is the reason it is a function rather than a line inside the snap. A
  `[:vis]` channel is keys and not dense, so reading one per node per frame would
  be cheap — but the snap needs the marks SORTED for a backward lookup, and
  building that per frame is what docs/animation-model.md forbids on the render
  path.

  PER POSE GROUP, because the thing being recovered is one group's closure. The
  alternative — snapping the slot itself, where the grid becomes a native frame —
  is one native frame for the whole picture, so a head would go two frames stale
  for one output frame to fix a mouth. A group's related parts share one answer,
  which is what `:pose-group` is already for: the mouth outline, its interior and
  the teeth reading different frames is the bug grouping prevents.

  No new signal and no new stored field: the cut `flow/freeze` already wrote is
  read where it lies, with its thresholding and hysteresis already decided. A
  group whose parts carry no closure cut gets no marks and no entry, which is
  correct for brows — there is no extreme brow position worth protecting."
  [nodes frames store]
  (when (and (integer? frames) (pos? frames))
    (not-empty
     (into {}
           (keep (fn [[group ns]]
                   (let [fs (into (sorted-set)
                                  (mapcat #(shut-frames (get (:channels %) [:vis])
                                                        frames store))
                                  ns)]
                     (when (seq fs) [group (vec fs)]))))
           (group-by #(or (:pose-group %) (:id %)) (vals nodes))))))

(defn- latest-mark
  "The largest mark at or before `f`, or nil. Binary search, as `held-frame` is:
  the marks are sorted once and read in whatever order the transport asks for."
  [ms f]
  (loop [lo 0 hi (dec (count ms)) hit nil]
    (if (> lo hi)
      (when (some? hit) (nth ms hit))
      (let [mid (bit-shift-right (+ lo hi) 1)]
        (if (<= (nth ms mid) f)
          (recur (inc mid) hi mid)
          (recur lo (dec mid) hit))))))

(defn snapped-frame
  "The native frame a grid slot reads: THE LATEST MARK IN `(lo, hi]`, AND `hi`
  WHEN THERE IS NONE.

  `hi` is the frame the slot defaults to — the latest native frame at or before
  its time — and `lo` is the frame the slot BEFORE it defaulted to. So the
  half-open interval is exactly the frames this slot is the first to cover, which
  are exactly the ones the grid shows to nobody. That one sentence is the whole
  mechanism: it recovers a dropped frame out of the slot's own gap, it can never
  read a frame another slot already showed, and it cannot reach past `hi`.

  BACKWARD ONLY, NEVER FORWARD, and note which direction that actually is because
  it is the easy thing to get wrong. A closure at native 13 in a 12-from-30 output
  is recovered by the slot whose default is 15, reading 13. It is NOT recovered by
  the slot whose default is 12 reaching forward to 13: that slot's instant is
  5/12s and native 13's is 13/30s, so it would show the closure 17ms before the
  mouth shut. A placement's frame is a floor, at-or-before, and `rate_test` asserts
  `selected <= f*speed` over every speed, so reaching forward
  would break a tested invariant as well as the no-lead rule. Reading 13 two
  native frames late is the lateness every hold already has.

  An EMPTY interval snaps nothing. A slot that reads what the slot before it read
  — a held exposure, or an output grid faster than the content — has no gap of its
  own, and `(hi, hi]` contains nothing to find.

  Takes no tolerance and will not grow one. The output rate or the exposure
  setting has already chosen the sparseness; the only question left is WHICH
  native frame an already-decided slot reads, and a second knob here would be a
  control with nothing to control."
  [marks group lo hi]
  (if-let [ms (get marks group)]
    (let [m (latest-mark ms hi)]
      (if (and (some? m) (> m lo)) m hi))
    hi))

(defn held-frame
  "Last value keyed at or before f, or default before the first key."
  [entries f default-frame]
  (loop [lo 0 hi (dec (count entries)) hit nil]
    (if (> lo hi)
      (if (some? hit) (second (nth entries hit)) default-frame)
      (let [mid (bit-shift-right (+ lo hi) 1)]
        (if (<= (first (nth entries mid)) f)
          (recur (inc mid) hi mid)
          (recur lo (dec mid) hit))))))

(defn source-frame
  "Read an explicit cut if one has happened; otherwise read the default pose.
  This keeps the existing motion before the first edited cut."
  [prepared group f default-frame]
  (if-let [entries (get prepared group)]
    (held-frame entries f default-frame)
    default-frame))

(defn put-cut
  "Set one held pose on an instance inside symbol `sid`. Earlier motion stays
  untouched."
  [clip sid instance group at source]
  (let [inst (get-in clip [:symbols sid :nodes instance])
        ;; A cut is checked against the ONE symbol this cel places. Which
        ;; drawing a lane shows is a question about the lane's other cels,
        ;; and each of them owns its own tracks — so there is nothing to union.
        placed (get-in clip [:symbols (node/source inst)])
        length (or (:frames placed) 0)
        active (filter (fn [n] (some :pose-sampled? (vals (:channels n))))
                       (vals (:nodes placed)))
        groups (set (map #(or (:pose-group %) (:id %)) active))
        ids (set (map :id active))]
    (when-not (and (= :instance (:kind inst))
                   (or (contains? groups group)
                       (and (vector? group) (= 2 (count group))
                            (= :node (first group))
                            (contains? ids (second group))))
                   (integer? at) (<= 0 at) (< at length)
                   (integer? source) (<= 0 source) (< source length))
      (throw (ex-info "invalid stage pose cut"
                      {:instance instance :group group :at at :source source})))
    (update-in clip [:symbols sid :nodes instance :playback :tracks group]
               #(assoc (or % {}) at source))))

(defn remove-cut
  "Remove a cut; an empty track again follows the normal generated motion."
  [clip sid instance group at]
  (let [path [:symbols sid :nodes instance :playback :tracks group]]
    (if-let [entries (get-in clip path)]
      (if-let [remaining (not-empty (dissoc entries at))]
        (assoc-in clip path remaining)
        (update-in clip [:symbols sid :nodes instance :playback :tracks]
                   dissoc group))
      clip)))

(defn problems
  "Errors in one symbol instance's pose tracks."
  [tracks source-frames groups]
  (cond
    (nil? tracks) []
    (not (map? tracks)) [":playback :tracks must be a map"]
    :else
    (vec
     (mapcat
      (fn [[group entries]]
        (cond
          (not (contains? groups group))
          [(str "pose track " (pr-str group) " names no generated shape")]

          (not (map? entries))
          [(str "pose track " (pr-str group) " must map local frames to source frames")]

          :else
          (concat
           (when-not (every? #(and (integer? %) (<= 0 %)
                                   (or (nil? source-frames) (< % source-frames)))
                             (keys entries))
             [(str "pose track " (pr-str group) " has an invalid change frame")])
           (when-not (every? #(and (integer? %) (<= 0 %)
                                   (or (nil? source-frames) (< % source-frames)))
                             (vals entries))
             [(str "pose track " (pr-str group) " names a pose outside the source")]))))
      tracks))))
